import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { GlobalNav } from "../shared/GlobalNav";
import { CreateProjectModal } from "../projects/CreateProjectModal";
import { formatDate, isOverdue, copyToClipboard, calculateVATInclusiveAmount, sendEmail } from "../../lib/appUtils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import {
  Calendar, CreditCard, FileText, Users, Copy, Check,
  ExternalLink, Edit3, Archive, Trash2, RotateCcw, AlertTriangle,
  Clock, CheckCircle, XCircle, ArrowRight, Settings, Plus, Link,
  Send, Shield, MoreHorizontal, Banknote,
  PiggyBank, Award, HelpCircle, Zap, TrendingUp, RefreshCw,
  Mail, AlertCircle, ChevronDown, Circle, Info, Crown, Lock, Signature, Eye, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ProjectCard Component - displays individual project in the list
const ProjectCard = ({ project, onUpdate, onClick }) => {
  const { toast } = useToast();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [projectToComplete, setProjectToComplete] = useState(null);

  const getPaymentMethodInfo = (project) => {
    const chosenStructure = project.client_chosen_structure || project.payment_structure;
    const paymentMethods = {
      full_upfront: { name: '100% Upfront', color: 'bg-purple-100 text-purple-700 border-purple-200', dotColor: 'bg-purple-500', icon: '💳' },
      fifty_fifty: { name: '50/50 Split', color: 'bg-green-100 text-green-700 border-green-200', dotColor: 'bg-green-500', icon: '⚖️' },
      three_month_milestones: { name: 'Monthly Installments', color: 'bg-blue-100 text-blue-700 border-blue-200', dotColor: 'bg-blue-500', icon: '📅' },
      credit_card: { name: 'Credit Card', color: 'bg-amber-100 text-amber-700 border-amber-200', dotColor: 'bg-amber-500', icon: '💎' },
      bnpl: { name: 'BNPL (Buy Now, Pay Later)', color: 'bg-pink-100 text-pink-700 border-pink-200', dotColor: 'bg-pink-500', icon: '⏰' },
      invoice: { name: 'Invoice (14 days)', color: 'bg-blue-100 text-blue-700 border-blue-200', dotColor: 'bg-blue-500', icon: '📄' }
    };
    return paymentMethods[chosenStructure] || { name: 'Payment Method TBD', color: 'bg-gray-100 text-gray-700 border-gray-200', dotColor: 'bg-gray-500', icon: '❓' };
  };
  
  const getStatusInfo = (project) => {
    const paymentMethod = getPaymentMethodInfo(project);
    if (project.status === 'archived') return { label: 'Archived', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' };
    if (project.status === 'completed') return { label: 'Work Delivered', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' };
    if (project.is_invoice_project && !project.client_chosen_structure) {
      const paidMilestones = project.milestones.filter(m => m.status === 'paid').length;
      const totalMilestones = project.milestones.length;
      const invoicePaid = project.invoice_paid_at || (paidMilestones > 0 && paidMilestones === totalMilestones);
      if (invoicePaid) return { label: 'Invoice Paid - Work In Progress', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' };
      return { label: 'Invoice Ready - Awaiting Payment', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' };
    }
    const contractSigned = project.contract_agreed;
    const paidMilestones = project.milestones.filter(m => m.status === 'paid').length;
    const totalMilestones = project.milestones.length;
    const allPaymentsReceived = paidMilestones === totalMilestones && totalMilestones > 0;
    if (!contractSigned) return { label: 'Awaiting Client Action', color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' };
    if (contractSigned && !allPaymentsReceived) return { label: `Contract Signed - ${paymentMethod.name} Selected`, color: paymentMethod.color, dotColor: paymentMethod.dotColor };
    if (allPaymentsReceived) {
      const chosenStructure = project.client_chosen_structure || project.payment_structure;
      if (chosenStructure === 'bnpl') return { label: `Contract Signed - ${paymentMethod.name} Selected`, color: paymentMethod.color, dotColor: paymentMethod.dotColor };
      return { label: `All Payments Received - Work In Progress`, color: paymentMethod.color, dotColor: paymentMethod.dotColor };
    }
    return { label: 'In Progress', color: paymentMethod.color, dotColor: paymentMethod.dotColor };
  };

  const handlePreviewInvoice = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoiceData(response.data);
      setShowInvoicePreview(true);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load invoice preview", variant: "destructive" });
    }
  };

  const handleSendInvoice = async () => {
    setSendingInvoice(true);
    try {
      await axios.post(`${API}/projects/${project.id}/invoice/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Invoice Sent", description: "Invoice has been sent to client for payment choice" });
      setShowInvoicePreview(false);
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to send invoice", variant: "destructive" });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleUnarchive = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/projects/${project.id}/unarchive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Project restored", description: "Project has been moved back to active projects" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to restore project", variant: "destructive" });
    }
  };

  const handleCompleteProject = async (projectId) => {
    setProjectToComplete(projectId);
    setShowCompleteDialog(true);
  };

  const confirmCompleteProject = async () => {
    try {
      await axios.post(`${API}/projects/${projectToComplete}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Work Completed", description: "Project has been marked as completed and moved to the Completed tab" });
      setShowCompleteDialog(false);
      setProjectToComplete(null);
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to complete project", variant: "destructive" });
    }
  };

  const handleReactivateProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/reactivate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Project Reactivated", description: "Project has been moved back to active projects" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to reactivate project", variant: "destructive" });
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Project Archived", description: "Project has been archived successfully" });
      onUpdate();
    } catch (error) {
      toast({ title: "Error", description: "Failed to archive project", variant: "destructive" });
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`${API}/projects/${project.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Project Deleted", description: "Project has been permanently deleted" });
      setShowDeleteDialog(false);
      onUpdate();
    } catch (error) {
      toast({ title: "Cannot Delete Project", description: error.response?.data?.detail || "Failed to delete project", variant: "destructive" });
      setShowDeleteDialog(false);
    }
  };

  const statusInfo = getStatusInfo(project);
  const paidAmount = project.milestones.filter(m => m.status === 'paid').reduce((sum, m) => sum + m.amount, 0);
  const totalWithVat = project.vat_enabled ? project.total_amount * (1 + (project.vat_rate || 20) / 100) : project.total_amount;
  const progress = Math.min((paidAmount / totalWithVat) * 100, 100);

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer" onClick={(e) => { if (!e.target.closest('button')) { onClick && onClick(e); } }}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 font-heading">{project.title}</h3>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${statusInfo.dotColor}`}></div>
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              </div>
              {!project.contract_agreed && project.status !== 'archived' && <Badge variant="outline" className="font-body">Contract pending</Badge>}
              {project.status === 'archived' && <button onClick={handleUnarchive} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-body">Restore</button>}
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-3 font-body">
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <div className="flex flex-col">
                  <span>{project.client_name}</span>
                  <span className="text-xs text-gray-500">{project.client_email}</span>
                </div>
              </div>
              <div>£{project.total_amount.toLocaleString()}</div>
              <div>Created {formatDate(project.created_at)}</div>
              {project.contract_signed && <div className="flex items-center space-x-1 text-green-600"><span className="w-4 h-4">📝</span><span className="text-xs font-medium">Signed</span></div>}
            </div>
            {project.milestones && project.milestones.length > 0 && (
              <div className="mb-3 p-2 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Payment Progress:</span>
                  <span className="text-xs text-gray-600">{project.milestones.filter(m => m.status === 'paid').length} of {project.milestones.length} payments received</span>
                </div>
                <div className="flex space-x-1">
                  {project.milestones.map((milestone, index) => (
                    <div key={milestone.id || index} className={`flex-1 h-2 rounded-full ${milestone.status === 'paid' ? 'bg-green-500' : milestone.status === 'pending' ? 'bg-yellow-300' : 'bg-gray-300'}`} title={`£${milestone.amount} - ${milestone.status}`} />
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm">
                  <Link className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-700 font-body">Direct Payment Link:</span>
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); copyToClipboard(project.hosted_url, toast, "Payment link copied - share with client"); }} className="text-green-600 hover:text-green-800 p-1 h-auto font-body">
                  <Copy className="w-3 h-3 mr-1" />Copy
                </Button>
              </div>
              <div className="text-xs font-mono text-green-800 mt-1 bg-white rounded px-2 py-1">{project.hosted_url}</div>
              <div className="text-xs text-green-700 mt-1 font-body">Send this link directly to clients for immediate payment access</div>
            </div>
            {user?.subscription_plan === 'embedded' && (
              <div className="mb-3 p-2 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm">
                    <Copy className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-700 font-body">Embed Reference Code:</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); copyToClipboard(project.slug, toast, "Embed reference copied - for website integration"); }} className="text-purple-600 hover:text-purple-800 p-1 h-auto font-body">
                    <Copy className="w-3 h-3 mr-1" />Copy
                  </Button>
                </div>
                <div className="text-xs font-mono text-purple-800 mt-1 bg-white rounded px-2 py-1">{project.slug}</div>
                <div className="text-xs text-purple-700 mt-1 font-body">Use this code for website payment widgets and embed integration</div>
              </div>
            )}
            <div className="mb-3">
              <div className="flex items-center space-x-2 text-sm">
                <PiggyBank className="w-4 h-4 text-green-600" />
                <span className="text-gray-600 font-body">{project.vat_enabled ? `VAT (${project.vat_rate}%): £${Math.round(project.total_amount * (project.vat_rate / 100)).toLocaleString()}` : 'VAT: Not applicable'}</span>
              </div>
            </div>
            {!project.is_invoice_project && project.milestones && project.milestones.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-700 text-sm font-body">Payment Progress</span>
                  </div>
                  <span className="text-xs text-blue-600 font-body">{project.milestones.filter(m => m.status === 'paid').length} of {project.milestones.length} paid</span>
                </div>
                <div className="space-y-2">
                  {project.milestones.map((milestone, index) => (
                    <div key={milestone.id || index} className="flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-2">
                        {milestone.status === 'paid' ? <CheckCircle className="w-3 h-3 text-green-600" /> : <Circle className="w-3 h-3 text-gray-400" />}
                        <span className="font-body text-gray-700">{milestone.title}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium font-body ${milestone.status === 'paid' ? 'text-green-600' : 'text-gray-600'}`}>£{milestone.amount?.toLocaleString() || '0'}</span>
                        {milestone.status === 'paid' && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Paid</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {progress > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600 font-body">
                  <span>£{paidAmount.toLocaleString()} received</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-green-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col space-y-2 ml-6">
            {(project.is_invoice_project || (project.invoice_number && project.invoice_number !== '')) && (
              <>
                <Button variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/projects/${project.id}?tab=invoice`); }} className="text-blue-600 hover:text-blue-900" title="Open Invoice Editor"><Edit3 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handlePreviewInvoice(); }} className="text-blue-600 hover:text-blue-900" title="Preview Invoice"><Eye className="w-4 h-4" /></Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (project.hosted_url) { window.open(project.hosted_url, '_blank'); } }} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2" title="View as Client"><ExternalLink className="w-4 h-4" /></Button>
            {((project.is_invoice_project) || (!project.is_invoice_project && project.milestones && project.milestones.length > 0)) && project.status !== 'completed' && (
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleCompleteProject(project.id); }} className={`${project.status === 'completed' ? 'text-green-600 hover:text-green-900' : 'text-gray-600 hover:text-green-600'}`} title="Mark as Completed"><CheckCircle className="w-4 h-4" /></Button>
            )}
            {project.status === 'completed' && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleReactivateProject(project.id); }} className="text-blue-600 hover:text-blue-900" title="Reactivate Project"><RotateCcw className="w-4 h-4" /></Button>}
            {project.contract_signed && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`${API}/contracts/${project.slug}/signed-pdf`, '_blank'); }} className="text-[#e6007e] hover:text-[#c4006b]" title="Download Signed Contract"><FileText className="w-4 h-4" /></Button>}
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleArchiveProject(project.id); }} className="text-yellow-600 hover:text-yellow-900" title="Archive Project"><Archive className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }} className="text-red-600 hover:text-red-900" title="Delete Project"><Trash2 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); sendEmail({ to: project.client_email, subject: `Payment Link for ${project.title}`, template: 'payment_link', project_id: project.id, payment_url: project.hosted_url }, toast); }} className="text-green-600 hover:text-green-900" title="Email Payment Link"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardContent>
      {showInvoicePreview && invoiceData && (
        <Dialog open={showInvoicePreview} onOpenChange={setShowInvoicePreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Invoice Preview - {invoiceData.invoice_number}</DialogTitle></DialogHeader>
            <div className="space-y-6">
              <div className="bg-white rounded-lg border shadow-sm"><div dangerouslySetInnerHTML={{ __html: invoiceData.invoice_content }} className="invoice-preview" /></div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600 font-body"><p>Invoice will be sent to: <strong>{project.client_email}</strong></p><p>Client will receive payment link to choose payment structure</p></div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setShowInvoicePreview(false)} className="font-body">Close Preview</Button>
                  <Button onClick={handleSendInvoice} disabled={sendingInvoice} className="bg-[#e6007e] hover:bg-[#c4006b] text-white font-body">{sendingInvoice ? "Sending..." : "Send to Client"}</Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Delete Project</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">Are you sure you want to delete <strong>{project.title}</strong>? This action cannot be undone.</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3"><div className="flex items-center space-x-2"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="text-sm text-red-800 font-body">All project data will be permanently lost.</span></div></div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="font-body">Cancel</Button>
                <Button onClick={handleDeleteProject} className="bg-red-600 hover:bg-red-700 text-white font-body">Delete Project</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {showCompleteDialog && (
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Mark Work as Complete</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">Are you sure you want to mark the work for <strong>{project.title}</strong> as delivered and complete?</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3"><div className="flex items-center space-x-2"><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-sm text-green-800 font-body">This will move the project to the "Completed" tab.</span></div></div>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => { setShowCompleteDialog(false); setProjectToComplete(null); }} className="font-body">Cancel</Button>
                <Button onClick={confirmCompleteProject} className="bg-green-600 hover:bg-green-700 text-white font-body">Mark as Complete</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ filter, onCreateProject }) => {
  if (filter === 'archived') {
    return (
      <Card className="border-2 border-dashed border-gray-300 text-center py-12">
        <CardContent>
          <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No archived projects</h3>
          <p className="text-gray-600 font-body">Archived projects will appear here when you archive them.</p>
        </CardContent>
      </Card>
    );
  }
  if (filter === 'fulfilled') {
    return (
      <Card className="border-2 border-dashed border-gray-300 text-center py-12">
        <CardContent>
          <CheckCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No fulfilled projects</h3>
          <p className="text-gray-600 font-body">Completed projects with all payments received will appear here.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-2 border-dashed border-gray-300 text-center py-12">
      <CardContent>
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">No chasing. All sorted.</h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto font-body">Structure your first payment flow. Choose your payment structure, lock it in with a contract, and send the payment link to your client.</p>
        <Button onClick={onCreateProject} className="bg-black hover:bg-gray-800 text-white font-body"><Plus className="w-4 h-4 mr-2" />Create Your First Project</Button>
      </CardContent>
    </Card>
  );
};

const CollapsibleCard = ({ title, subtitle, children, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-heading">{title}</CardTitle>
            {subtitle && <p className="text-gray-600 text-xs font-body mt-1">{subtitle}</p>}
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-gray-500 transition-transform ${
              isExpanded ? 'transform rotate-180' : ''
            }`} 
          />
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

// How It Works Sidebar Panel Component (Non-collapsible)
const HowItWorksSidebar = () => {
  const steps = [
    {
      id: 1,
      title: 'Create project',
      description: 'Set up project details and scope',
      color: 'bg-black'
    },
    {
      id: 2,
      title: 'Send payment link to client',
      description: 'Share secure checkout page or embed code',
      color: 'bg-blue-600'
    },
    {
      id: 3,
      title: 'Client chooses payment structure',
      description: 'Client selects upfront, split, instalment, credit card or BNPL (soon)',
      color: 'bg-green-600'
    },
    {
      id: 4,
      title: 'Client signs contract',
      description: 'Generate contract and get client signature',
      color: 'bg-purple-600'
    },
    {
      id: 5,
      title: 'Confirm payment and start work',
      description: 'Track instalments and project progress',
      color: 'bg-[#e6007e]'
    }
  ];

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-heading">How it works</CardTitle>
        <p className="text-gray-600 text-xs font-body mt-1">5-step process from project to payment</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {steps.map((step) => (
            <div key={step.id} className="flex items-start space-x-3">
              <div className={`w-6 h-6 ${step.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <span className="text-white text-xs font-bold">{step.id}</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm font-heading">{step.title}</h4>
                <p className="text-xs text-gray-600 font-body">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Overdue Payments Stats Card for main dashboard
const OverduePaymentsStatsCard = () => {
  const [protectionEnabled, setProtectionEnabled] = useState(true);
  const [overdueData, setOverdueData] = useState({ total: 0, count: 0 });
  const { token } = useAuth();

  useEffect(() => {
    const fetchOverdueStats = async () => {
      try {
        const response = await axios.get(`${API}/overdue-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.summary) {
          setOverdueData({
            total: response.data.summary.total_overdue_amount || 0,
            count: response.data.summary.overdue_count || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch overdue stats:', error);
      }
    };

    if (token) {
      fetchOverdueStats();
    }
  }, [token]);

  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className="p-1.5 md:p-2 bg-red-100 rounded-lg flex-shrink-0">
              <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-red-600" />
            </div>
            <div className="ml-3 md:ml-4 min-w-0">
              <p className="text-xl md:text-2xl font-bold text-gray-900 font-heading">£{overdueData.total.toLocaleString()}</p>
              <p className="text-xs md:text-sm text-gray-600 font-body">Overdue payments</p>
            </div>
          </div>
          <div className="flex flex-col items-end flex-shrink-0 ml-2">
            <span className="text-xs text-gray-500 font-body mb-1 hidden sm:block">Protection</span>
            <button
              onClick={() => setProtectionEnabled(!protectionEnabled)}
              className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
                protectionEnabled ? 'bg-red-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  protectionEnabled ? 'translate-x-4' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        {protectionEnabled && overdueData.count > 0 && (
          <div className="text-xs text-red-600 font-body">
            {overdueData.count} overdue payment{overdueData.count !== 1 ? 's' : ''} protected
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced VAT Vault Section with Toggle and Navigation
const VATVaultSectionWithToggle = ({ stats }) => {
  const [autoSeparation, setAutoSeparation] = useState(true);
  const navigate = useNavigate();

  return (
    <Card 
      className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200"
      onClick={() => navigate('/vat-vault')}
    >
      <CardContent className="p-4 md:p-8 text-center">
        <div className="p-2 md:p-4 bg-purple-600 rounded-full w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 flex items-center justify-center">
          <PiggyBank className="w-5 h-5 md:w-8 md:h-8 text-white" />
        </div>
        <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 font-heading">VAT Vault</h3>
        <p className="text-xl md:text-2xl font-bold text-purple-600 mb-1 md:mb-2 font-heading">
          £{Math.round(stats?.vatVaultTotal || 0).toLocaleString()}
        </p>
        <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4 font-body">VAT amount calculated and set aside</p>
        
        <div className="flex items-center justify-center space-x-2 mt-2 md:mt-4 pt-2 md:pt-3 border-t border-purple-200">
          <span className="text-xs text-gray-600 font-body hidden md:inline">Auto-separation</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setAutoSeparation(!autoSeparation);
            }}
            className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors ${
              autoSeparation ? 'bg-purple-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                autoSeparation ? 'translate-x-4' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-xs font-body ${autoSeparation ? 'text-purple-600' : 'text-gray-500'}`}>
            {autoSeparation ? 'ON' : 'OFF'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
const OurPaymentOptionsPanel = () => {
  const paymentOptions = [
    {
      id: 'full_upfront',
      title: '100% Upfront',
      description: 'Full payment before work begins',
      icon: <Banknote className="w-5 h-5" />
    },
    {
      id: 'fifty_fifty',
      title: '50/50 Split',
      description: 'Half upfront, half on completion',
      icon: <TrendingUp className="w-5 h-5" />
    },
    {
      id: 'three_month_milestones',
      title: 'Monthly Instalments',
      description: '3-month payment schedule',
      icon: <Calendar className="w-5 h-5" />
    },
    {
      id: 'credit_card',
      title: 'Credit Card Payment',
      description: 'Pay in full by credit card',
      icon: <CreditCard className="w-5 h-5" />
    },
    {
      id: 'bnpl',
      title: 'BNPL',
      description: 'Coming Soon',
      icon: <Clock className="w-5 h-5" />,
      comingSoon: true
    }
  ];

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-heading">Our Payment Options</CardTitle>
        <p className="text-gray-600 text-xs font-body mt-1">Flexible payment structures for your business</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3">
          {paymentOptions.map((option) => (
            <div
              key={option.id}
              data-testid={`payment-option-${option.id}`}
              className={`p-3 text-center border border-gray-200 rounded-lg ${
                option.comingSoon ? 'col-span-2 bg-gray-50 opacity-80' : ''
              }`}
            >
              <div className={`mb-2 flex justify-center ${option.comingSoon ? 'text-gray-400' : 'text-black'}`}>
                {option.icon}
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 font-heading text-xs">{option.title}</h3>
              <p className="text-xs text-gray-600 font-body">{option.description}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
const PaymentOptionsPanel = ({ onCreateProject }) => {
  const paymentOptions = [
    {
      id: 'full_upfront',
      title: '100% Upfront',
      description: 'Full payment before work begins',
      icon: <Banknote className="w-6 h-6" />
    },
    {
      id: 'fifty_fifty',
      title: '50/50 Split',
      description: 'Half upfront, half on completion',
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      id: 'three_month_milestones',
      title: 'Monthly Instalments',
      description: '3-month payment schedule',
      icon: <Calendar className="w-6 h-6" />
    },
    {
      id: 'credit_card',
      title: 'Credit Card Payment',
      description: 'Pay in full by credit card',
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      id: 'bnpl',
      title: 'BNPL',
      description: 'Coming Soon',
      icon: <Clock className="w-6 h-6" />,
      comingSoon: true
    }
  ];

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-heading">Choose your payment structure</CardTitle>
        <p className="text-gray-600 text-sm font-body">Select the payment flow that works best for your business</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {paymentOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => onCreateProject(option.id)}
              className="p-4 text-left border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all group"
            >
              <div className="text-black mb-2 group-hover:text-[#e6007e]">
                {option.icon}
              </div>
              <h3 className="font-semibold text-gray-900 mb-1 font-heading text-sm">{option.title}</h3>
              <p className="text-xs text-gray-600 font-body">{option.description}</p>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Contracts Panel Component (NEW)
const ContractsPanel = ({ projects }) => {
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const { token } = useAuth();
  const { toast } = useToast();
  
  const activeProjects = projects.filter(p => p.status !== 'archived');
  
  const getContractStatus = (project) => {
    if (project.contract_agreed) {
      return {
        status: 'Signed',
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        date: formatDate(project.contract_signed_at)
      };
    } else {
      return {
        status: 'Pending',
        color: 'text-yellow-600', 
        bgColor: 'bg-yellow-100',
        date: null
      };
    }
  };

  const handleViewContract = async (project) => {
    try {
      // If contract is signed, fetch the signed PDF version
      if (project.contract_signed && project.signed_contract_url) {
        const response = await axios.get(`${API}${project.signed_contract_url}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContractContent(response.data);
      } else {
        // For unsigned contracts, fetch the regular preview
        const response = await axios.get(`${API}/checkout/${project.slug}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContractContent(response.data.contract.content);
      }
      
      setSelectedProject(project);
      setShowContractModal(true);
    } catch (error) {
      console.error('Error loading contract:', error);
      toast({
        title: "Error",
        description: "Failed to load contract",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-heading">Contracts</span>
          </CardTitle>
          <p className="text-gray-600 text-sm font-body">Quick view of contract statuses</p>
        </CardHeader>
        <CardContent>
          {activeProjects.length === 0 ? (
            <p className="text-gray-500 text-sm font-body">No active projects</p>
          ) : (
            <div className="space-y-3">
              {activeProjects.slice(0, 4).map((project) => {
                const contractStatus = getContractStatus(project);
                return (
                  <div 
                    key={project.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleViewContract(project)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate font-body">
                        {project.title}
                      </p>
                      <p className="text-xs text-gray-500 font-body">
                        v{project.contract_version}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${contractStatus.bgColor} ${contractStatus.color} font-body`}>
                        {contractStatus.status}
                      </span>
                      <FileText className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                );
              })}
              {activeProjects.length > 4 && (
                <p className="text-xs text-gray-500 font-body pt-2 border-t">
                  +{activeProjects.length - 4} more contracts
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contract Modal */}
      {showContractModal && selectedProject && (
        <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {selectedProject.contract_agreed ? '✓ Signed Contract' : 'Contract Preview'} - {selectedProject.title}
              </DialogTitle>
            </DialogHeader>
            <div className="bg-white rounded-lg border shadow-sm">
              <div 
                dangerouslySetInnerHTML={{ __html: contractContent }}
                className="contract-preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// VAT Vault Section
const VATVaultSection = () => {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center space-x-2">
          <PiggyBank className="w-5 h-5 text-green-600" />
          <span className="font-heading">VAT Vault</span>
          <Badge variant="secondary" className="text-xs font-body">Coming Soon</Badge>
        </CardTitle>
        <p className="text-gray-600 text-sm font-body">Keep tax money separate automatically so it's ready when you need it.</p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
          <div>
            <p className="text-sm font-semibold text-green-900 font-body">VAT Rate</p>
            <p className="text-2xl font-bold text-green-900 font-heading">20%</p>
          </div>
          <div className="text-green-600">
            <Shield className="w-8 h-8" />
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-3 font-body">
          Automatic VAT separation ensures compliance and cash flow protection.
        </p>
      </CardContent>
    </Card>
  );
};

// Trust Badge Section with Black and Gold Peace of Mind Container - Paify Plus Feature
const TrustBadgeSection = () => {
  const [enabled, setEnabled] = useState(true);
  const [showTrustBadge, setShowTrustBadge] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState('full');
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Check if user has Paify Plus or Embedded subscription
  const hasPlusSubscription = user?.subscription_plan === 'paidd_plus' || user?.subscription_plan === 'embedded';

  const fullBadgeCode = `<!-- Paify Trust Badge - Full -->
<div style="display:inline-block;background:linear-gradient(135deg,#e6007e,#ff4da6);border-radius:16px;padding:24px 32px;text-align:center;font-family:system-ui,sans-serif;box-shadow:0 8px 24px rgba(230,0,126,0.3);border:3px solid white;max-width:320px;">
  <div style="background:white;border-radius:50%;width:60px;height:60px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e6007e" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  </div>
  <div style="color:white;font-weight:800;font-size:18px;margin-bottom:12px;letter-spacing:1px;">VERIFIED & TRUSTED</div>
  <div style="background:rgba(255,255,255,0.2);border-radius:8px;padding:12px;margin-bottom:16px;">
    <div style="color:white;font-weight:700;font-size:14px;">PAIFY CERTIFIED</div>
    <div style="color:rgba(255,255,255,0.9);font-size:12px;">Professional Payment Processing</div>
  </div>
  <div style="color:white;font-size:13px;text-align:left;display:inline-block;">
    <div style="margin-bottom:6px;">✓ Secure Payments</div>
    <div style="margin-bottom:6px;">✓ Contract Protection</div>
    <div>✓ 24/7 Support</div>
  </div>
  <div style="border-top:1px solid rgba(255,255,255,0.3);margin-top:16px;padding-top:12px;">
    <div style="color:rgba(255,255,255,0.9);font-size:11px;">Your business and payments are fully protected</div>
  </div>
</div>`;

  const simpleBadgeCode = `<!-- Paify Trust Badge - Simple -->
<div style="display:inline-block;background:linear-gradient(135deg,#e6007e,#ff4da6);border-radius:12px;padding:16px 24px;text-align:center;font-family:system-ui,sans-serif;box-shadow:0 4px 12px rgba(230,0,126,0.3);">
  <div style="display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:8px;">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
    <span style="color:white;font-weight:600;font-size:14px;">Verified by Paify</span>
  </div>
  <div style="color:rgba(255,255,255,0.9);font-size:11px;">Secure • Protected • Paid</div>
</div>`;

  const embedCode = selectedBadge === 'full' ? fullBadgeCode : simpleBadgeCode;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Embed code copied to clipboard"
    });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Card className={`border-0 shadow-sm ${!hasPlusSubscription ? 'opacity-70' : ''}`}>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <Award className="w-5 h-5 text-blue-600" />
            <span className="font-heading">Trust Badge</span>
            <Badge className="bg-purple-100 text-purple-700 text-xs">Paify Plus</Badge>
          </CardTitle>
          <p className="text-gray-600 text-sm font-body">Display Paify verification on your payment pages</p>
        </CardHeader>
        <CardContent>
          {!hasPlusSubscription ? (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Award className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900 font-body">Paify Plus Feature</span>
                </div>
                <div className="text-xs text-purple-800 space-y-1 font-body">
                  <div>✅ Display verification badge</div>
                  <div>✅ Embed on your website</div>
                  <div>✅ Build client trust</div>
                </div>
              </div>
              
              <Button
                onClick={() => window.location.href = '/subscriptions'}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body"
              >
                Upgrade now
              </Button>
              
              <p className="text-xs text-gray-600 font-body text-center">
                Available with Paify Plus or Embedded subscription
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700 font-body">Enable Trust Badge</span>
                <button
                  onClick={() => setEnabled(!enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-[#e6007e]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              {enabled && (
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-900 font-body">Verified by Paify</span>
                    </div>
                    <p className="text-xs text-blue-700 font-body">Secure • Protected • Paid</p>
                  </div>
                  
                  <Button 
                    onClick={() => setShowTrustBadge(true)}
                    variant="outline"
                    size="sm"
                    className="w-full font-body"
                  >
                    <Award className="w-3 h-3 mr-2" />
                    View Trust Badge
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Peace of Mind Container - Brand Colors (Pink & White) - Only show for Plus users */}
      {hasPlusSubscription && (
        <div className="bg-gradient-to-br from-[#e6007e] via-[#ff4da6] to-[#e6007e] rounded-lg p-6 text-white shadow-lg border-2 border-white">
        <div className="flex justify-center mb-4">
          <div className="bg-white rounded-full p-3">
            <Shield className="w-8 h-8 text-[#e6007e]" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-center mb-4 font-heading text-white">PEACE OF MIND</h3>
        <div className="grid grid-cols-3 gap-4 text-center mb-4">
          <div>
            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-white" />
            <p className="text-xs font-body text-white">Secure</p>
          </div>
          <div>
            <Shield className="w-5 h-5 mx-auto mb-1 text-white" />
            <p className="text-xs font-body text-white">Protected</p>
          </div>
          <div>
            <Award className="w-5 h-5 mx-auto mb-1 text-white" />
            <p className="text-xs font-body text-white">Professional</p>
          </div>
        </div>
        <div className="text-center border-t border-white border-opacity-30 pt-3">
          <p className="text-xs text-white text-opacity-90 font-body">
            Your business and payments are fully protected
          </p>
        </div>
      </div>
      )}

      {/* Trust Badge Modal */}
      {showTrustBadge && hasPlusSubscription && (
        <Dialog open={showTrustBadge} onOpenChange={setShowTrustBadge}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-center">Official Trust Badge</DialogTitle>
            </DialogHeader>
            
            {/* Badge Style Selection */}
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={() => setSelectedBadge('full')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedBadge === 'full' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Full Badge
              </button>
              <button
                onClick={() => setSelectedBadge('simple')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedBadge === 'simple' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Simple Badge
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
              {/* Badge Preview */}
              <div className="flex items-center justify-center">
                {selectedBadge === 'full' ? (
                  <div className="bg-gradient-to-br from-[#e6007e] to-[#ff4da6] rounded-xl p-6 text-white text-center shadow-2xl border-4 border-white" style={{maxWidth: '320px'}}>
                    <div className="flex justify-center mb-4">
                      <div className="bg-white rounded-full p-3">
                        <Shield className="w-8 h-8 text-[#e6007e]" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold mb-3 tracking-wide font-heading">VERIFIED & TRUSTED</h3>
                    <div className="bg-white bg-opacity-20 rounded-lg p-3 mb-4">
                      <p className="text-sm font-bold font-heading">PAIFY CERTIFIED</p>
                      <p className="text-xs opacity-90 font-body">Professional Payment Processing</p>
                    </div>
                    <div className="space-y-1 text-sm text-left inline-block">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-body">Secure Payments</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-body">Contract Protection</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-body">24/7 Support</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-white border-opacity-30">
                      <p className="text-xs opacity-90 font-body">
                        Your business and payments are fully protected
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-[#e6007e] to-[#ff4da6] rounded-xl p-4 text-white text-center shadow-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Shield className="w-5 h-5" />
                      <span className="font-semibold text-sm font-body">Verified by Paify</span>
                    </div>
                    <p className="text-xs opacity-90 font-body">Secure • Protected • Paid</p>
                  </div>
                )}
              </div>
              
              {/* Embed Code Section */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm font-heading">Embed on your website</h4>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={copyEmbedCode}
                    className="font-body"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-2" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
                <div className="bg-gray-900 rounded-lg p-3 flex-1 overflow-auto max-h-64">
                  <pre className="text-xs text-green-400 whitespace-pre-wrap font-mono">{embedCode}</pre>
                </div>
                <p className="text-xs text-gray-500 mt-2 font-body">
                  Paste this code anywhere on your website to display the Trust Badge.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Invoice Insurance Section - Add-on Feature  
const InvoiceInsuranceSection = () => {
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-blue-600" />
          <span className="font-heading">Invoice Insurance</span>
          <Badge variant="outline" className="text-xs font-body text-blue-600 border-blue-600">Add-on</Badge>
          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Coming Soon
          </span>
        </CardTitle>
        <p className="text-gray-600 text-sm font-body">Comprehensive protection against payment defaults and disputes.</p>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Coming Soon Overlay */}
          <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <p className="font-semibold text-gray-900 font-heading">Coming Soon</p>
              <p className="text-sm text-gray-600 font-body mt-1">Invoice insurance is on the way!</p>
            </div>
          </div>
          
          <div className="opacity-50">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700 font-body">Enable Insurance Protection</span>
              <button
                disabled
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-gray-200 cursor-not-allowed"
              >
                <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
              </button>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm font-semibold text-blue-900 font-body">Coverage Active</p>
                <p className="text-2xl font-bold text-blue-900 font-heading">0%</p>
                <p className="text-xs text-blue-700 font-body">No coverage active</p>
              </div>
              <div className="text-blue-600">
                <Shield className="w-8 h-8" />
              </div>
            </div>
            
            <p className="text-xs text-gray-600 mt-3 font-body">
              Protect your business against payment defaults and client disputes with comprehensive insurance coverage.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Widget Unlock Section (Non-collapsible) - Now based on Embedded subscription
const WidgetUnlockSection = () => {
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Check if user has Embedded subscription
  const hasEmbeddedPlan = user?.subscription_plan === 'embedded';

  const generateBusinessIdentifier = (businessName) => {
    return businessName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  };

  const businessId = generateBusinessIdentifier(user?.business_name || '');
  const iframeCode = `<iframe src="${window.location.origin}/embed-business/${businessId}" width="100%" height="700" frameborder="0" style="border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"></iframe>`;

  return (
    <>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <Zap className="w-5 h-5 text-purple-600" />
            <span className="font-heading">Website Widget</span>
            <Badge className={hasEmbeddedPlan ? "bg-green-100 text-green-700" : "bg-purple-100 text-purple-700"} >
              {hasEmbeddedPlan ? "Active" : "Embedded"}
            </Badge>
          </CardTitle>
          <p className="text-gray-600 text-sm font-body">
            {hasEmbeddedPlan 
              ? "Add your services to any website with one line of code." 
              : "Embed your services catalog on any website instantly."
            }
          </p>
        </CardHeader>
        <CardContent>
          {!hasEmbeddedPlan ? (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900 font-body">Embedded Plan Feature</span>
                </div>
                <div className="text-xs text-purple-800 space-y-1 font-body">
                  <div>✅ Unlimited website embeds</div>
                  <div>✅ Auto-updates with new services</div>
                  <div>✅ Custom branding support</div>
                  <div>✅ Works on any website (iframe)</div>
                </div>
              </div>
              
              <Button
                onClick={() => window.location.href = '/subscriptions'}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body"
              >
                Upgrade now
              </Button>
              
              <p className="text-xs text-gray-600 font-body text-center">
                Available with the Embedded subscription plan
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-900 font-body">Widget Active</span>
                </div>
                <p className="text-xs text-green-800 font-body">
                  Your embed code is ready. Paste it on any website to show your services.
                </p>
              </div>
              
              <Button
                onClick={() => setShowWidgetModal(true)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy website widget
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Widget Modal */}
      <Dialog open={showWidgetModal} onOpenChange={setShowWidgetModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center space-x-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span>Website Widget Code</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600 font-body">
              Copy and paste this code into your website to embed your services catalog.
            </p>
            
            <div>
              <Label className="text-xs font-body text-gray-600 mb-2 block">
                Iframe Embed Code:
              </Label>
              <div className="relative">
                <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto font-mono whitespace-pre-wrap break-all">
{iframeCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(iframeCode);
                    toast({ title: "Copied!", description: "Iframe code copied to clipboard" });
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-white"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800 space-y-1 font-body">
              <div><strong>Your Business ID:</strong> {businessId}</div>
              <div><strong>Custom Colors:</strong> Add ?accent=#your-hex to the URL</div>
              <div><strong>Responsive:</strong> Width 100%, height 700px recommended</div>
            </div>
            
            <Button
              onClick={() => {
                navigator.clipboard.writeText(iframeCode);
                toast({ title: "Copied!", description: "Iframe code copied to clipboard" });
                setShowWidgetModal(false);
              }}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-body"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Code & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Generic Collapsible Wrapper for existing components
const CollapsibleWrapper = ({ title, subtitle, component, defaultExpanded = false }) => {
  return (
    <CollapsibleCard 
      title={title} 
      subtitle={subtitle}
      defaultExpanded={defaultExpanded}
    >
      <div className="space-y-2">
        {component}
      </div>
    </CollapsibleCard>
  );
};

// Invoice Dashboard Section
// Collapsible wrapper for existing components
const CollapsibleInvoiceSection = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/invoice/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Invoice Marked Paid",
        description: "Invoice has been marked as paid and project completed"
      });
      
      fetchInvoices();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark invoice as paid",
        variant: "destructive"
      });
    }
  };

  const content = loading ? (
    <div className="text-center py-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#e6007e] mx-auto"></div>
      <p className="mt-2 text-xs text-gray-600 font-body">Loading...</p>
    </div>
  ) : invoices.length === 0 ? (
    <div className="text-center py-4">
      <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
      <p className="text-sm text-gray-600 font-body">No invoices yet</p>
      <p className="text-xs text-gray-500 font-body">Create invoice projects to see them here</p>
    </div>
  ) : (
    <div className="space-y-3">
      {invoices.slice(0, 3).map((invoice) => (
        <div key={invoice.id} className="border border-gray-200 rounded-lg p-3">
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 text-sm font-heading truncate">
                {invoice.title}
              </h4>
              <p className="text-xs text-gray-600 font-body">£{invoice.total_amount}</p>
            </div>
            <Badge variant="secondary" className="ml-2 text-xs">
              {invoice.status}
            </Badge>
          </div>
          <div className="flex justify-between items-center text-xs text-gray-500">
            <span className="font-body">Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
            {invoice.status === 'pending' && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleMarkPaid(invoice.project_id)}
                className="text-xs h-6"
              >
                Mark Paid
              </Button>
            )}
          </div>
        </div>
      ))}
      {invoices.length > 3 && (
        <p className="text-xs text-gray-500 text-center font-body">
          +{invoices.length - 3} more invoices
        </p>
      )}
    </div>
  );

  return (
    <CollapsibleCard 
      title="Invoices" 
      subtitle={`${invoices.length} active invoice${invoices.length !== 1 ? 's' : ''}`}
      defaultExpanded={false}
    >
      {content}
    </CollapsibleCard>
  );
};

const InvoiceDashboardSection = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (projectId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/invoice/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Invoice Marked Paid",
        description: "Invoice has been marked as paid and project completed"
      });
      
      fetchInvoices(); // Refresh list
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to mark invoice as paid",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-heading">Invoices</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-2 text-xs text-gray-600 font-body">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-blue-600" />
          <span className="font-heading">Invoices</span>
          <Badge variant="secondary" className="text-xs font-body">{invoices.length}</Badge>
        </CardTitle>
        <p className="text-gray-600 text-sm font-body">Manage your project invoices</p>
      </CardHeader>
      <CardContent>
        {invoices.length === 0 ? (
          <div className="text-center py-4">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-body">No invoices yet</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-48 overflow-y-auto">
            {invoices.slice(0, 5).map((invoice) => (
              <div key={invoice.project_id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-xs">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate font-body">{invoice.title}</p>
                  <p className="text-gray-600 font-body">
                    {invoice.invoice_number} • Due {formatDate(invoice.due_date)}
                  </p>
                </div>
                <div className="text-right ml-2">
                  <p className="font-bold text-gray-900 font-heading">£{invoice.total_amount.toLocaleString()}</p>
                  {invoice.status === 'paid' ? (
                    <span className="text-green-600 font-body">Paid ✓</span>
                  ) : invoice.status === 'overdue' ? (
                    <span className="text-red-600 font-body">{invoice.days_overdue}d late</span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkPaid(invoice.project_id)}
                      className="text-xs p-1 h-auto font-body"
                    >
                      Mark Paid
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {invoices.length > 5 && (
              <p className="text-xs text-gray-500 font-body text-center pt-2 border-t">
                +{invoices.length - 5} more invoices
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
const OverduePaymentsPanel = () => {
  const [overdueData, setOverdueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchOverduePayments();
  }, []);

  const fetchOverduePayments = async () => {
    try {
      const response = await axios.get(`${API}/overdue-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverdueData(response.data);
    } catch (error) {
      console.error('Failed to fetch overdue payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadEvidence = async (projectId, projectTitle) => {
    try {
      const response = await axios.get(`${API}/overdue-payments/${projectId}/evidence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Evidence Pack Ready",
        description: `Evidence pack for ${projectTitle} has been generated successfully.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate evidence pack",
        variant: "destructive"
      });
    }
  };

  const handleExportOverdue = () => {
    if (!overdueData?.overdue_payments.length) return;
    
    const csvContent = [
      "Project,Client,Amount,Days Late,Interest Accrued,Recovery Fee,Total Owed",
      ...overdueData.overdue_payments.map(payment => 
        `"${payment.project_title}","${payment.client_name}",£${payment.amount},${payment.days_late},£${payment.interest_accrued},£${payment.recovery_fee},£${payment.total_owed}`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `overdue-payments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Export Complete",
      description: "Overdue payments exported to CSV successfully."
    });
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-heading">Overdue Payments</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-2 text-xs text-gray-600 font-body">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { summary, overdue_payments } = overdueData || { summary: null, overdue_payments: [] };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="font-heading">Overdue Payments</span>
          {summary?.protection_enabled && (
            <Badge className="bg-red-100 text-red-700 text-xs font-body">Protection ON</Badge>
          )}
        </CardTitle>
        <p className="text-gray-600 text-sm font-body">Monitor and manage late payments</p>
      </CardHeader>
      <CardContent>
        {!overdue_payments.length ? (
          <div className="text-center py-4">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-body">No overdue payments</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Stats */}
            <div className="bg-red-50 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-red-900 font-body">Outstanding</p>
                  <p className="text-lg font-bold text-red-900 font-heading">£{summary?.total_overdue_amount.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-red-900 font-body">Interest Accrued</p>
                  <p className="text-lg font-bold text-red-900 font-heading">£{summary?.total_interest_accrued.toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-red-700 font-body">
                {summary?.overdue_count} overdue payment{summary?.overdue_count !== 1 ? 's' : ''} • {summary?.interest_rate}% interest rate
              </div>
            </div>

            {/* Overdue List */}
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {overdue_payments.slice(0, 3).map((payment) => (
                <div key={payment.milestone_id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate font-body">{payment.project_title}</p>
                    <p className="text-gray-600 font-body">{payment.days_late} days late</p>
                  </div>
                  <div className="text-right ml-2">
                    <p className="font-bold text-red-600 font-heading">£{payment.total_owed.toLocaleString()}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownloadEvidence(payment.project_id, payment.project_title)}
                      className="text-xs p-1 h-auto font-body"
                    >
                      Evidence
                    </Button>
                  </div>
                </div>
              ))}
              {overdue_payments.length > 3 && (
                <p className="text-xs text-gray-500 font-body text-center pt-2 border-t">
                  +{overdue_payments.length - 3} more overdue
                </p>
              )}
            </div>

            {/* Export Option */}
            <Button
              onClick={handleExportOverdue}
              variant="outline"
              size="sm"
              className="w-full text-xs font-body"
            >
              <Copy className="w-3 h-3 mr-1" />
              Export Overdue Report
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



// Peace of Mind Section (Final sidebar component)
const PeaceOfMindSection = () => {
  return (
    <CollapsibleCard 
      title="Peace of Mind" 
      subtitle="Built-in protection and support"
      defaultExpanded={false}
    >
      <div className="space-y-3">
        <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
          <Shield className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-green-900 text-sm font-heading">Secure Payments</h4>
            <p className="text-xs text-green-700 font-body">End-to-end encryption and fraud protection</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
          <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 text-sm font-heading">Contract Protection</h4>
            <p className="text-xs text-blue-700 font-body">Legally binding agreements with audit trail</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
          <HelpCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-purple-900 text-sm font-heading">24/7 Support</h4>
            <p className="text-xs text-purple-700 font-body">Expert help when you need it most</p>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500 font-body">
            🛡️ Your business and payments are fully protected
          </p>
        </div>
      </div>
    </CollapsibleCard>
  );
};

// =====================================================================
// Onboarding banners (shown above the dashboard)
//   1. Setup banner — appears until business details + email config are complete
//   2. Free-plan banner — always shown for `standard` plan users (dismissible per session)
//
// Special-case: the `demo@paify.io` account ALWAYS sees both banners on every
// login (so prospects can see what they look like). They can still dismiss
// each one per-session, but the dismissal is never persisted.
// =====================================================================
const DEMO_ACCOUNT_EMAIL = 'demo@paify.io';

const OnboardingBanners = ({ user, userSettings }) => {
  const navigate = useNavigate();
  const isDemoAccount = (user?.email || '').toLowerCase() === DEMO_ACCOUNT_EMAIL;

  const [dismissedUpgrade, setDismissedUpgrade] = useState(() => {
    if (isDemoAccount) return false;
    return sessionStorage.getItem('paify_upgrade_banner_dismissed') === '1';
  });
  const [dismissedSetup, setDismissedSetup] = useState(false);

  // Setup completeness checks. We only show the banner once the settings call
  // has resolved so the alert doesn't briefly flash for users who actually
  // have everything configured.
  const settingsLoaded = userSettings !== null && userSettings !== undefined;
  const hasBusinessName = Boolean(user?.business_name && user.business_name.trim());
  const hasBusinessEmail = Boolean(userSettings?.business_email);
  const hasSmtpHost = Boolean(userSettings?.smtp_host);
  const needsSetupReal =
    settingsLoaded && (!hasBusinessName || !hasBusinessEmail || !hasSmtpHost);

  // Demo account always sees the setup banner (forced on every login).
  const needsSetup = isDemoAccount ? !dismissedSetup : (needsSetupReal && !dismissedSetup);

  const isFreePlan = user?.subscription_plan === 'standard';
  // Demo account always sees the upgrade banner regardless of plan.
  const showUpgrade = isDemoAccount ? !dismissedUpgrade : (isFreePlan && !dismissedUpgrade);

  if (!needsSetup && !showUpgrade) return null;

  const handleDismissUpgrade = () => {
    if (!isDemoAccount) {
      sessionStorage.setItem('paify_upgrade_banner_dismissed', '1');
    }
    setDismissedUpgrade(true);
  };

  const handleDismissSetup = () => {
    setDismissedSetup(true);
  };

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 space-y-3"
      data-testid="onboarding-banners"
    >
      {needsSetup && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 font-body"
          data-testid="setup-banner"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">Finish setting up your account</p>
            <p>
              To start using Paify, please set up your{' '}
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="underline font-semibold text-amber-900 hover:text-amber-700"
                data-testid="setup-banner-link"
              >
                Business Details &amp; Email Configuration
              </button>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismissSetup}
            className="text-amber-700 hover:text-amber-900 ml-2"
            aria-label="Dismiss"
            data-testid="setup-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {showUpgrade && (
        <div
          className="flex items-start gap-3 rounded-lg border border-pink-200 bg-gradient-to-r from-pink-50 to-rose-50 px-4 py-3 text-sm text-gray-800 font-body"
          data-testid="upgrade-banner"
        >
          <Zap className="w-5 h-5 mt-0.5 text-[#e6007e] shrink-0" />
          <div className="flex-1">
            <p>
              To use Paify&apos;s full potential, upgrade to{' '}
              <button
                type="button"
                onClick={() => navigate('/subscriptions')}
                className="underline font-semibold text-[#e6007e] hover:text-[#c4006b]"
                data-testid="upgrade-banner-link"
              >
                Paify Plus
              </button>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismissUpgrade}
            className="text-gray-400 hover:text-gray-600 ml-2"
            aria-label="Dismiss"
            data-testid="upgrade-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [preselectedStructure, setPreselectedStructure] = useState('');
  const [filter, setFilter] = useState('active');
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractContent, setContractContent] = useState('');
  const [userSettings, setUserSettings] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    position: '',
    agreed_to_sign: false
  });
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open the Create Project modal when arriving with ?create=<structure>.
  // Used by the Invoices page "New Invoice" CTA (?create=invoice) so the
  // create flow opens pre-selected to the right payment structure.
  useEffect(() => {
    const createParam = searchParams.get('create');
    if (createParam) {
      setPreselectedStructure(createParam);
      setShowCreateForm(true);
      // Strip the param so refreshes don't re-open the modal.
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch VAT data from backend API
  const fetchVATData = async () => {
    try {
      const response = await axios.get(`${API}/vat-vault`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVatData(response.data);
    } catch (error) {
      console.error('Failed to fetch VAT data:', error);
    }
  };

  // Fetch subscription features
  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscription/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription features:', error);
    }
  };

  // Handle subscription upgrade
  const handleUpgradeSubscription = async (planType) => {
    try {
      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        plan_id: planType,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to start upgrade process. Please try again.",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    if (token) {
      fetchSubscriptionFeatures();
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
    fetchQuotes();
    fetchUserSettings();
    fetchVATData();
  }, [filter]);

  // Auto-refresh whenever the user returns to the tab so payments / signatures
  // that happened while away show up immediately without a manual refresh.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        fetchProjects();
        fetchVATData();
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    // Light polling so a client signing/paying in another tab is reflected
    // within ~20 seconds even if the user doesn't switch focus.
    const interval = setInterval(refresh, 8000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      clearInterval(interval);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    try {
      // Always pull every project so the dashboard can decide locally what
      // belongs in each tab. We still default the "Active" tab to a sensible
      // working set, but invoice projects stay visible even after they're
      // marked complete (so paid invoices don't vanish on the user).
      const response = await axios.get(`${API}/projects?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allProjects = response.data || [];
      // Keep a full copy so cards like the "Invoices" counter can count every
      // invoice project the user has, regardless of the current tab filter.
      setAllProjects(allProjects);

      let filteredProjects = allProjects;
      if (filter === 'active') {
        // Active = anything not archived, not completed — EXCEPT invoice
        // projects, which we keep visible even after they're completed so
        // users can always find them from the main dashboard.
        filteredProjects = allProjects.filter(p =>
          p.status !== 'archived' &&
          (p.status !== 'completed' || p.is_invoice_project)
        );
      } else if (filter === 'completed') {
        filteredProjects = allProjects.filter(p => p.status === 'completed');
      } else if (filter === 'archived') {
        filteredProjects = allProjects.filter(p => p.status === 'archived');
      }

      // Sort projects by creation date - newest first
      filteredProjects.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setProjects(filteredProjects);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch projects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      // Don't show error toast for quotes since it's supplementary data
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  const handleCreateProject = (structure = '') => {
    setPreselectedStructure(structure);
    setShowCreateForm(true);
  };

  const handleSignatureSetup = async () => {
    try {
      if (!signatureData.full_name || !signatureData.position) {
        toast({
          title: "Missing Information",
          description: "Please provide your full name and position.",
          variant: "destructive"
        });
        return;
      }

      if (!signatureData.agreed_to_sign) {
        toast({
          title: "Agreement Required",
          description: "You must agree to electronically sign all contracts.",
          variant: "destructive"
        });
        return;
      }

      const response = await axios.post(
        `${API}/user/signature-setup`,
        signatureData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast({
          title: "Signature Setup Complete",
          description: "Your signature will now be applied to all contracts.",
        });
        
        // Refresh user settings
        await fetchUserSettings();
        
        setShowSignatureModal(false);
      }
    } catch (error) {
      console.error('Signature setup error:', error);
      toast({
        title: "Setup Failed",
        description: error.response?.data?.detail || "Failed to set up signature. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Statuses that should NEVER contribute to dashboard money totals.
  // Mirrors the backend VAT Vault exclusion list so figures reconcile.
  const EXCLUDED_STATUSES = ['archived', 'deleted', 'cancelled', 'canceled', 'refunded', 'draft'];
  const visibleProjects = projects.filter(p => !EXCLUDED_STATUSES.includes((p.status || '').toLowerCase()));

  // Sum every milestone gross amount across all visible projects.
  // milestone.amount IS the gross (net + VAT) when vat_enabled, so this is
  // the authoritative source of "money expected to flow through Paify".
  const sumMilestones = (filterFn) =>
    visibleProjects.reduce((sum, p) => {
      const ms = (p.milestones || []).filter(filterFn);
      return sum + ms.reduce((mSum, m) => mSum + (m.amount || 0), 0);
    }, 0);

  const grossTotalValue = sumMilestones(() => true);
  const grossPaidValue = sumMilestones(m => m.status === 'paid');
  const grossOutstandingValue = Math.max(0, grossTotalValue - grossPaidValue);

  const stats = {
    total: projects.length,
    active: visibleProjects.length,
    totalValue: grossTotalValue,
    paidValue: grossPaidValue,
    outstandingValue: grossOutstandingValue,
    // Use VAT data from backend API (single source of truth)
    vatVaultTotal: vatData?.paid_vat || 0,
    outstandingVat: (vatData?.pending_vat || 0) + (vatData?.overdue_vat || 0),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="dashboard" />

      <OnboardingBanners user={user} userSettings={userSettings} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Page Header - Mobile Optimized */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2 font-heading">Dashboard</h1>
              <p className="text-sm md:text-base text-gray-600 font-body">Structure your payment flow with confidence.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button 
                onClick={() => handleCreateProject()}
                className="bg-black hover:bg-gray-800 text-white font-body w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
              {(user?.subscription_plan === 'paidd_plus' || user?.subscription_plan === 'embedded') && (
                <Button 
                  variant="outline"
                  onClick={() => navigate('/subscriptions-manage')}
                  className="font-body w-full sm:w-auto border-black text-black hover:bg-gray-100"
                  data-testid="dashboard-subscription-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Subscriptions
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Overview - 2 columns on mobile, 4 on tablet/desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center">
                <div className="p-1.5 md:p-2 bg-black rounded-lg flex-shrink-0">
                  <FileText className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <div className="ml-3 md:ml-4 min-w-0">
                  <p className="text-xl md:text-2xl font-bold text-gray-900 font-heading">{stats.active}</p>
                  <p className="text-xs md:text-sm text-gray-600 font-body">Active projects</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center">
                <div className="p-1.5 md:p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <Banknote className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                </div>
                <div className="ml-3 md:ml-4 min-w-0">
                  <p className="text-xl md:text-2xl font-bold text-gray-900 font-heading">£{Math.round(stats.paidValue || 0).toLocaleString()}</p>
                  <p className="text-xs md:text-sm text-gray-600 font-body">Payments received</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center">
                <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                </div>
                <div className="ml-3 md:ml-4 min-w-0">
                  <p className="text-xl md:text-2xl font-bold text-gray-900 font-heading">£{Math.round(stats.outstandingValue || 0).toLocaleString()}</p>
                  <p className="text-xs md:text-sm text-gray-600 font-body">Outstanding</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <OverduePaymentsStatsCard />
        </div>

        {/* Main Feature Boxes - 2 columns on mobile/tablet, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200"
            onClick={() => navigate('/quotes')}
          >
            <CardContent className="p-4 md:p-8 text-center">
              <div className="p-2 md:p-4 bg-yellow-600 rounded-full w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 flex items-center justify-center">
                <FileText className="w-5 h-5 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 font-heading">Quotes</h3>
              <p className="text-xl md:text-2xl font-bold text-yellow-600 mb-0.5 md:mb-1 font-heading">
                {quotes.filter(q => q.status !== 'converted').length}
              </p>
              <p className="text-xs md:text-sm text-gray-600 font-body">Active quotes</p>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200"
            onClick={() => navigate('/invoices')}
          >
            <CardContent className="p-4 md:p-8 text-center">
              <div className="p-2 md:p-4 bg-blue-600 rounded-full w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 flex items-center justify-center">
                <FileText className="w-5 h-5 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 font-heading">Invoices</h3>
              <p className="text-xl md:text-2xl font-bold text-blue-600 mb-0.5 md:mb-1 font-heading">
                {allProjects.filter(p => (p.is_invoice_project || (p.invoice_number && p.invoice_number !== '')) && p.status !== 'archived').length}
              </p>
              <p className="text-xs md:text-sm text-gray-600 font-body">Invoices issued</p>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200"
            onClick={() => navigate('/contracts')}
          >
            <CardContent className="p-4 md:p-8 text-center">
              <div className="p-2 md:p-4 bg-green-600 rounded-full w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 flex items-center justify-center">
                <Signature className="w-5 h-5 md:w-8 md:h-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2 font-heading">Contracts</h3>
              <p className="text-xl md:text-2xl font-bold text-green-600 mb-0.5 md:mb-1 font-heading">
                {allProjects.filter(p => p.contract_agreed && p.status !== 'archived').length}
              </p>
              <p className="text-xs md:text-sm text-gray-600 font-body">Active contracts</p>
            </CardContent>
          </Card>

          <VATVaultSectionWithToggle stats={stats} />
        </div>



        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-8">
          {/* Projects List */}
          <div className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 font-heading">
                {filter === 'active' ? 'Active Projects' :
                 filter === 'completed' ? 'Completed Projects' : 
                 filter === 'archived' ? 'Archived Projects' : 'All Projects'}
              </h2>
              <div className="flex rounded-md shadow-sm overflow-x-auto">
                <button
                  onClick={() => setFilter('active')}
                  className={`px-2 md:px-3 py-1 text-xs md:text-sm font-medium rounded-l-md border font-body whitespace-nowrap ${
                    filter === 'active' 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setFilter('completed')}
                  className={`px-2 md:px-3 py-1 text-xs md:text-sm font-medium border-t border-b font-body whitespace-nowrap ${
                    filter === 'completed' 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setFilter('archived')}
                  className={`px-2 md:px-3 py-1 text-xs md:text-sm font-medium border-t border-b font-body whitespace-nowrap ${
                    filter === 'archived' 
                      ? 'bg-black text-white border-black' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Archived
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-2 md:px-3 py-1 text-xs md:text-sm font-medium rounded-r-md border font-body whitespace-nowrap ${
                      filter === 'all' 
                        ? 'bg-black text-white border-black' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    All
                  </button>
              </div>
            </div>

            {projects.length === 0 ? (
              <EmptyState filter={filter} onCreateProject={() => handleCreateProject()} />
            ) : (
              <div className="space-y-4">
                {projects.map((project) => (
                  <ProjectCard 
                    key={project.id} 
                    project={project} 
                    onUpdate={fetchProjects}
                    onClick={() => navigate(`/projects/${project.id}`)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <HowItWorksSidebar />
            <OurPaymentOptionsPanel />
            <WidgetUnlockSection />
            <InvoiceInsuranceSection />
            <TrustBadgeSection />
          </div>
        </div>
      </main>

      {showCreateForm && (
        <CreateProjectModal
          isOpen={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
            setPreselectedStructure('');
          }}
          preselectedStructure={preselectedStructure}
          onSuccess={(createdProject) => {
            fetchProjects();
            setShowCreateForm(false);
            setPreselectedStructure('');
            
            // If it's an invoice project, show additional success message with open button
            if (createdProject && createdProject.is_invoice_project) {
              setTimeout(() => {
                toast({
                  title: "Ready to Edit Invoice",
                  description: "Click below to open and edit your invoice details",
                  action: (
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate(`/projects/${createdProject.id}?tab=invoice`);
                      }}
                      className="ml-2"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Open Invoice
                    </Button>
                  )
                });
              }, 1000);
            }
          }}
        />
      )}

      {/* Signature Setup Modal */}
      {showSignatureModal && (
        <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {userSettings?.signature_setup_completed ? '✍️ Update Your Signature' : '✍️ Set Up Your Signature'}
              </DialogTitle>
              <p className="text-sm text-gray-600 font-body mt-2">
                Your signature will be applied to all contracts you create. This is a one-time setup that enables legally binding electronic signatures.
              </p>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Full Legal Name *
                </label>
                <input
                  type="text"
                  value={signatureData.full_name}
                  onChange={(e) => setSignatureData({ ...signatureData, full_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-body"
                  placeholder="e.g., John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-body">
                  Position/Title *
                </label>
                <input
                  type="text"
                  value={signatureData.position}
                  onChange={(e) => setSignatureData({ ...signatureData, position: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-body"
                  placeholder="e.g., Director, CEO, Owner"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={signatureData.agreed_to_sign}
                    onChange={(e) => setSignatureData({ ...signatureData, agreed_to_sign: e.target.checked })}
                    className="mt-1 mr-3"
                  />
                  <label className="text-sm text-gray-700 font-body">
                    <strong>I agree to electronically sign all contracts</strong> created through this platform. 
                    I understand that my electronic signature is legally binding under UK Electronic Communications Act 2000.
                  </label>
                </div>
              </div>

              {userSettings?.signature_setup_completed && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-body">
                    ✓ Signature previously set up on {new Date(userSettings.signature_setup_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSignatureModal(false)}
                className="font-body"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSignatureSetup}
                className="bg-black hover:bg-gray-800 text-white font-body"
                disabled={!signatureData.full_name || !signatureData.position || !signatureData.agreed_to_sign}
              >
                {userSettings?.signature_setup_completed ? 'Update Signature' : 'Set Up Signature'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Contract Viewing Modal */}
      {showContractModal && (
        <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {selectedContract?.contract_agreed ? '✓ Signed Contract' : 'Contract Preview'} - {selectedContract?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div 
                dangerouslySetInnerHTML={{ __html: contractContent }}
                className="contract-preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Upgrade Required</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Quotes are available with <strong>Paify Plus</strong> subscription or higher.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Crown className="w-4 h-4 text-yellow-600" />
                  <span className="font-medium text-yellow-800 font-body">Paify Plus Features</span>
                </div>
                <ul className="mt-2 text-sm text-yellow-700 space-y-1 font-body">
                  <li>• Create professional quotes</li>
                  <li>• Send invoices to clients</li>
                  <li>• Add your branding & logo</li>
                  <li>• VAT receipts & reporting</li>
                  <li>• Trust badge verification</li>
                </ul>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowUpgradeModal(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    setShowUpgradeModal(false);
                    handleUpgradeSubscription('paidd_plus');
                  }}
                  className="bg-[#e6007e] hover:bg-pink-700 text-white font-body"
                >
                  Upgrade to Paify Plus
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Enhanced Project Card Component

export default Dashboard;
